<script>
  import { API_BASE_URL } from './config.js';
  import ErrorMessage from './components/common/ErrorMessage.svelte';
  import Button from './components/common/Button.svelte';
  import InfoModal from './components/modals/InfoModal.svelte';
  
  export let setPage;
  export let setUser;
  
  // 로그인 폼 입력값
  let email = '';
  let password = '';
  let isLoading = false;
  let errorMessage = '';
  
  // 탈퇴한 회원 모달 상태
  let showDeletedUserModal = false;
  
  // 로그인 요청 처리 함수
  async function handleLogin(event) {
    event.preventDefault();
    
    // 입력값 검증
    if (!email || !password) {
      errorMessage = '이메일과 비밀번호를 모두 입력해주세요.';
      return;
    }
    
    try {
      isLoading = true;
      errorMessage = '';
      
      // 서버로 로그인 요청 보내기
      const response = await fetch(`${API_BASE_URL}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });
      
      // 응답 처리
      const data = await response.json();
      
      if (!response.ok) {
        // 탈퇴한 회원인 경우 모달 표시
        if (data.code === 'DELETED_USER') {
          showDeletedUserModal = true;
          return;
        }
        errorMessage = data.error || '로그인에 실패했습니다.';
        return;
      }
      
      console.log('로그인 응답 데이터:', data); // 디버깅 로그
      
      // 로그인 성공 시 토큰과 사용자 정보 저장
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      // 저장된 토큰 확인
      console.log('저장된 토큰:', localStorage.getItem('token')); // 디버깅 로그
      
      // 사용자 상태 업데이트
      setUser(data.user);

      // 로그인 성공 후 강력 새로고침 (배포된 새 버전 적용)
      // URL을 #projects로 변경한 후 새로고침하여 프로젝트 페이지 표시
      setTimeout(() => {
        window.location.href = window.location.href.split('#')[0] + '#projects';
      }, 500);
    } catch (error) {
      console.error('로그인 오류:', error);
      errorMessage = '서버 연결에 실패했습니다. 다시 시도해주세요.';
    } finally {
      isLoading = false;
    }
  }
  
  async function handlePasswordReset() {
    if (!email) {
      errorMessage = '비밀번호를 초기화할 이메일 주소를 입력해주세요.';
      return;
    }

    isLoading = true;
    errorMessage = '';

    try {
      // 1단계: 이메일 존재 여부 먼저 확인
      const checkResponse = await fetch(`${API_BASE_URL}/api/users/check-exists?email=${encodeURIComponent(email)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      const checkData = await checkResponse.json();

      if (!checkResponse.ok || !checkData.exists) {
        errorMessage = '이메일이 존재하지 않습니다.';
        isLoading = false;
        return;
      }

      // 2단계: 이메일이 존재하는 경우에만 발송 확인
      if (!confirm(`${email} 주소로 임시 비밀번호를 발송할까요?`)) {
        isLoading = false;
        return;
      }

      // 3단계: 실제 비밀번호 초기화 및 이메일 발송
      const resetResponse = await fetch(`${API_BASE_URL}/api/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const resetData = await resetResponse.json();

      if (!resetResponse.ok) {
        errorMessage = resetData.error || '비밀번호 초기화에 실패했습니다.';
      } else {
        alert('이메일로 임시 비밀번호가 발송되었습니다. 확인 후 로그인해주세요.');
        password = ''; // 비밀번호 필드 초기화
      }

    } catch (error) {
      console.error('비밀번호 초기화 오류:', error);
      errorMessage = '서버와 통신 중 오류가 발생했습니다.';
    } finally {
      isLoading = false;
    }
  }

  // 탈퇴한 회원 모달 확인 처리
  function handleDeletedUserModalOk() {
    showDeletedUserModal = false;
    email = '';
    password = '';
    errorMessage = '';
  }
</script>

<div class="login-container">
  <div class="login-card">
    <div class="login-header">
      <h1 class="login-title text-primary">WIDE-WORK</h1>
      <p class="login-subtitle">WIDE-WORK에 오신 것을 환영합니다.</p>
      <h2 class="login-section-title">로그인</h2>
    </div>
    
    <ErrorMessage message={errorMessage} type="error" />
    
    <form class="login-form" on:submit={handleLogin}>
      <div class="form-group mb-4">
        <label for="email" class="form-label">이메일(ID)</label>
        <input 
          type="email" 
          id="email" 
          class="form-input"
          bind:value={email}
          placeholder="이메일을 입력하세요"
          required
        >
      </div>
      
      <div class="form-group mb-4">
        <label for="password" class="form-label">비밀번호</label>
        <input 
          type="password" 
          id="password" 
          class="form-input"
          bind:value={password}
          placeholder="비밀번호를 입력하세요"
          required
        >
      </div>
      
      <Button 
        type="submit" 
        variant="primary" 
        size="lg" 
        fullWidth={true}
        loading={isLoading}
        disabled={isLoading}
      >
        {isLoading ? '로그인 중...' : 'WIDE-WORK 시작하기'}
      </Button>
    </form>
    
    <div class="login-footer text-center">
      <p class="mb-2 text-muted">
        아직 회원이 아니신가요? 
        <a href="/signup" class="text-primary" on:click|preventDefault={() => setPage('signup')}>회원가입</a>
      </p>
      
      <p class="mb-4">
        <a href="#reset-password" class="text-muted text-sm" on:click|preventDefault={handlePasswordReset}>비밀번호 초기화</a>
      </p>
      
      <Button variant="primary" size="sm" on:click={() => setPage('home')}>
        ← 메인으로 돌아가기
      </Button>
    </div>
  </div>
</div>

<!-- 탈퇴한 회원 안내 모달 -->
<InfoModal 
  bind:isOpen={showDeletedUserModal}
  title="로그인 불가"
  message="탈퇴한 회원입니다."
  type="warning"
  okText="확인"
  onOk={handleDeletedUserModalOk}
/>

<!-- CSS 모듈 시스템 사용 --> 